/**
 * #4923 — the event feedback payload, kept apart from the React form so it is unit-tested.
 *
 * It posts to www `/api/leads/event-feedback` (#4919), which verifies Turnstile and
 * forwards an `event_feedback` submit to the CRM. The testimonial is sent only when the
 * person ticks the consent box; without consent it is never part of the payload.
 */

export const EVENT_FEEDBACK_ENDPOINT = '/api/leads/event-feedback';

/**
 * The public Turnstile site key is served, never hardcoded (#4919): www-sbi reads the
 * TURNSTILE_SITE_KEY Secrets Store binding. While the route answers non-200 the form
 * shows "Feedback opens soon" and never posts.
 */
export const TURNSTILE_SITE_KEY_ENDPOINT = '/api/leads/turnstile-site-key';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface EventFeedbackInput {
  eventSlug: string;
  name: string;
  email: string;
  rating: number | null;
  mostUseful: string;
  recommend: boolean | null;
  testimonial: string;
  testimonialConsent: boolean;
  turnstileToken: string;
}

export interface EventFeedbackPayload {
  name: string;
  email: string;
  answers: Record<string, number | boolean | string>;
  turnstile_token: string;
}

/** `/events/<slug>/feedback` → slug, else undefined. */
export function slugFromFeedbackPath(pathname: string): string | undefined {
  const m = /^\/events\/([^/?#]+)\/feedback\/?$/.exec(pathname);
  if (!m) return undefined;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    // A malformed escape such as %E0 is not a slug: show the 404, not the error page.
    return undefined;
  }
}

/** The Turnstile calls the form uses after a failed send. */
export interface TurnstileResetter {
  reset: (widgetId?: string) => void;
}

/**
 * #4923 — after any failed send, reset the Turnstile widget: a token is single-use, so
 * a retry with the spent token gets 403 until a reload. The caller also clears its
 * stored token. Returns the copy key: 429 (the #5049 rate limit) gets its own message.
 */
export function onSendFailure(
  status: number | null,
  turnstile: TurnstileResetter | undefined,
  widgetId: string | undefined,
): 'errorTooMany' | 'errorGeneric' {
  turnstile?.reset(widgetId);
  return status === 429 ? 'errorTooMany' : 'errorGeneric';
}

/** Build the payload, or null when a required field is missing or invalid. */
export function buildEventFeedbackPayload(input: EventFeedbackInput): EventFeedbackPayload | null {
  const name = input.name.trim();
  const email = input.email.trim();
  if (!input.eventSlug || !name || !EMAIL_REGEX.test(email)) return null;
  if (input.rating === null || !Number.isInteger(input.rating) || input.rating < 1 || input.rating > 5) {
    return null;
  }

  const answers: Record<string, number | boolean | string> = {
    event: input.eventSlug.slice(0, 500),
    rating: input.rating,
  };
  const mostUseful = input.mostUseful.trim();
  if (mostUseful) answers.most_useful = mostUseful.slice(0, 500);
  if (input.recommend !== null) answers.would_recommend = input.recommend;
  const testimonial = input.testimonial.trim();
  if (testimonial && input.testimonialConsent) {
    answers.testimonial = testimonial.slice(0, 500);
    answers.testimonial_consent = true;
  }

  return { name, email, answers, turnstile_token: input.turnstileToken };
}
